import { useCallback, useEffect, useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";
import dayjs from "dayjs";

import { useAuth } from "@/features/authentication";
import {
  AddCustomerFormDialog,
  EditCustomerFormDialog,
  genderLabels,
} from "@/features/customers";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const Customers = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("customers", {
        params: {
          ...paginationParams,
          ...(keyword && { search: keyword }),
        },
      });
      setCustomers(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pelanggan"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_CUSTOMERS) && (
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama/email/nomor pelanggan"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={customers}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 3,
              sortable: false,
            },
            {
              field: "gender",
              headerName: "Jenis Kelamin",
              flex: 2,
              sortable: false,
              valueGetter: (value) => (value ? genderLabels[value] : "-"),
            },
            {
              field: "age",
              headerName: "Umur (Tahun)",
              flex: 2,
              sortable: false,
              valueGetter: (value, row) =>
                row.dateOfBirth
                  ? dayjs().diff(dayjs(row.dateOfBirth), "year")
                  : "-",
            },
            {
              field: "email",
              headerName: "Email",
              flex: 3,
              sortable: false,
              valueGetter: (value) => value || "-",
            },
            {
              field: "phoneNumbers",
              headerName: "No. HP",
              flex: 2,
              sortable: false,
              valueGetter: (value) =>
                value.map((p) => `+${p.dialCode}${p.number}`).join(", "),
            },
            ...(hasPermission(PermissionEnum.UPDATE_CUSTOMERS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: ({ id }) => [
                      <IconButton
                        key="edit"
                        onClick={() => setCustomerId(id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        {isAddDialogOpen && (
          <AddCustomerFormDialog
            onClose={() => setIsAddDialogOpen(false)}
            onSubmitted={loadCustomers}
          />
        )}
        {!!customerId && (
          <EditCustomerFormDialog
            customerId={customerId}
            onClose={() => setCustomerId(null)}
            onSubmitted={loadCustomers}
          />
        )}
      </Stack>
    </PageContainer>
  );
};
